import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ProjectRestService} from '../../../api/services/project.rest.service';
import {QueryParamsApiModel} from '../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {ProjectApiModel} from '../../../api/models/project.api-model';
import {ProjectCollaborationDataProviderService} from './project-collaboration.data-provider.service';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {MediaObjectRestService} from '../../../api/services/media-object.rest.service';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {finalize, map, switchMap} from 'rxjs/operators';
import {ResponseNotificationService} from '../../../core/services/response-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalInterface} from '../../../shared/modal/modal.interface';
import {ModalStateEvent} from '../../../shared/modal/modal-state-event';
import {ModalState} from '../../../shared/modal/modal-state.enum';
import {ModalComponent} from '../../../shared/modal/modal.component';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {MediaUploaderComponent} from '../../../shared/media/media-uploader/media-uploader.component';
import {
  CollaborationTypeEnum,
  COLLABORATION_TYPES,
  SERVICE_COLLABORATION_TYPES
} from '../../../core/enums/collaboration-type.enum';
import {UploadTaskTrackerService} from '../../../core/services/upload-task-tracker.service';

interface ProjectOption {
  id: string;
  label: string;
  project: ProjectApiModel;
}

@Component({
  selector: 'app-collaboration-create',
  templateUrl: './collaboration-create.component.html',
  styleUrls: ['./collaboration-create.component.scss']
})
export class CollaborationCreateComponent implements OnInit, ModalInterface {
  public data: any;
  public parent?: ModalComponent;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;

  public currentUser: CurrentUserViewModel;
  public form: UntypedFormGroup;
  public projectOptions: ProjectOption[] = [];
  public filteredProjectOptions: ProjectOption[] = [];
  public typeOptions: SelectItem[] = [];
  public isLoading = false;
  @ViewChild('mediaUploaderComponent') public mediaUploaderComponent: MediaUploaderComponent;

  private readonly types: CollaborationTypeEnum[] = COLLABORATION_TYPES;
  private defaultProjectIri: string | null = null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly projectRS: ProjectRestService,
    private readonly collaborationDPS: ProjectCollaborationDataProviderService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
    private readonly confirmationService: ConfirmationService,
    private readonly uploadTaskTracker: UploadTaskTrackerService,
  ) {
  }

  get isModalMode(): boolean {
    return !!this.parent && !!this.subject && !!this.state;
  }

  get projectOptionControl(): AbstractControl {
    return this.form.get('projectOption');
  }

  get selectedType(): string {
    return this.form.get('type').value;
  }

  ngOnInit(): void {
    this.currentUser = this.data?.currentUserVM || this.route.snapshot.data?.currentUser as CurrentUserViewModel;
    this.defaultProjectIri = this.data?.defaultProjectIri ?? this.route.snapshot.queryParamMap.get('project');
    this.createForm();
    this.buildTypeOptions();
    this.bindModalState();
    this.fetchProjects();
  }

  public beforeModalCloseAttempt(): Promise<boolean> | boolean {
    if (!this.isModalMode || !this.hasUnsavedChanges()) {
      return true;
    }

    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: this.translate.instant('shared.modal.modal_service.add_collaboration.exit_process_title'),
        message: this.translate.instant('shared.modal.modal_service.add_collaboration.exit_process_message'),
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  private createForm(): void {
    this.form = this.fb.group({
      projectOption: new UntypedFormControl(null, [Validators.required]),
      type: new UntypedFormControl('PHOTO_SESSION', [Validators.required]),
      title: new UntypedFormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]),
      shortDescription: new UntypedFormControl('', [Validators.maxLength(255)]),
      description: new UntypedFormControl('', [Validators.maxLength(10000)]),
      initialMediaDescription: new UntypedFormControl('', [Validators.maxLength(10000)]),
      initialMediaHistoryContent: new UntypedFormControl('', [Validators.maxLength(10000)]),
      serviceSummary: new UntypedFormControl('', [Validators.maxLength(5000)]),
      createHistoryEntryOnApproval: new UntypedFormControl(false),
      historyStartDate: new UntypedFormControl(null),
      historyEndDate: new UntypedFormControl(null),
      initialMediaCreateHistoryEntryOnApproval: new UntypedFormControl(false),
      initialMediaHistoryStartDate: new UntypedFormControl(null),
      initialMediaHistoryEndDate: new UntypedFormControl(null),
      initialMediaCopyDescriptionToHistoryContent: new UntypedFormControl(false),
    });
  }

  private bindModalState(): void {
    if (!this.isModalMode) {
      return;
    }

    this.subject.next(new ModalStateEvent(ModalState.INVALID));
    this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.submit();
      }
    });
    this.form.valueChanges.subscribe(() => this.updateModalValidityState());
  }

  private buildTypeOptions(): void {
    this.typeOptions = this.types.map((type) => ({
      value: type,
      label: this.translate.instant('features.user.collaboration.types.' + type),
    }));
  }

  private fetchProjects(): void {
    const query = new QueryParamsApiModel();
    query.limit = 100;
    query.pagination = false;
    this.projectRS.getCollection(query)
      .pipe(map((resultList: ResultListApiModel<ProjectApiModel>) => {
        return resultList.records
          .filter((project) => this.resolveUserSlug(project.user) !== this.currentUser?.slug)
          .map((project) => ({
            id: project['@id'],
            label: this.getProjectLabel(project),
            project,
          }));
      }))
      .subscribe((projectOptions) => {
        this.projectOptions = projectOptions;
        this.filteredProjectOptions = projectOptions;
        this.applyDefaultProjectSelection();
        this.updateModalValidityState();
      });
  }

  private applyDefaultProjectSelection(): void {
    if (!this.defaultProjectIri) {
      return;
    }

    const option = this.projectOptions.find((projectOption) => projectOption.id === this.defaultProjectIri);
    if (option) {
      this.form.patchValue({projectOption: option}, {emitEvent: false});
    }
  }

  public searchProjects(event: {query: string}): void {
    const query = (event?.query || '').trim().toLowerCase();
    if (!query) {
      this.filteredProjectOptions = [...this.projectOptions];
      return;
    }

    this.filteredProjectOptions = this.projectOptions.filter((projectOption) => {
      return projectOption.label.toLowerCase().includes(query);
    });
  }

  public isServiceType(type: string): boolean {
    return SERVICE_COLLABORATION_TYPES.includes(type as CollaborationTypeEnum);
  }

  public getProjectLabel(project: ProjectApiModel): string {
    const vehicle: any = project.vehicle as any;
    const user: any = project.user as any;
    const vehicleLabel = typeof vehicle === 'string'
      ? project.slug
      : (vehicle?.fullVehicleIdentity || project.slug);
    const ownerLabel = typeof user === 'string'
      ? user.split('/').pop()
      : (user?.username || user?.slug || 'owner');

    return `${vehicleLabel}${project.alias ? ` (${project.alias})` : ''} · @${ownerLabel}`;
  }

  public submit(): void {
    if (!this.form.valid || !this.projectOptionControl?.value) {
      this.form.markAllAsTouched();
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.error_required'));
      this.updateModalValidityState();
      return;
    }

    if (this.isLoading) {
      return;
    }

    if (this.uploadTaskTracker.hasPendingTasks()) {
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.error'));
      return;
    }

    const projectOption = this.projectOptionControl.value as ProjectOption;
    const type = this.form.get('type').value as string;
    const title = (this.form.get('title').value || '').trim();
    const shortDescription = (this.form.get('shortDescription').value || '').trim();
    const description = (this.form.get('description').value || '').trim();
    const initialMediaDescription = (this.form.get('initialMediaDescription').value || '').trim();
    const initialMediaHistoryContent = (this.form.get('initialMediaHistoryContent').value || '').trim();
    const copyDescriptionToHistory = !!this.form.get('initialMediaCopyDescriptionToHistoryContent')?.value;
    const serviceSummary = (this.form.get('serviceSummary').value || '').trim();
    const createHistoryEntryOnApproval = !!this.form.get('createHistoryEntryOnApproval').value;
    const historyStartDate = this.form.get('historyStartDate').value || null;
    const historyEndDate = this.form.get('historyEndDate').value || null;
    const initialMediaCreateHistoryEntryOnApproval = createHistoryEntryOnApproval;
    const initialMediaHistoryStartDate = historyStartDate;
    const initialMediaHistoryEndDate = historyEndDate;
    const initialMediaHistoryContentResolved = copyDescriptionToHistory
      ? (initialMediaHistoryContent || serviceSummary || initialMediaDescription || description)
      : initialMediaHistoryContent;

    this.isLoading = true;
    if (this.isModalMode) {
      this.subject.next(new ModalStateEvent(ModalState.PROCESS));
    }
    this.uploadInitialMedia()
      .pipe(
        switchMap((mediaIris) => {
          const payload = {
            project: projectOption.id,
            type,
            title,
            shortDescription: shortDescription || null,
            description: description || null,
            serviceSummary: this.isServiceType(type) ? (serviceSummary || null) : null,
            createHistoryEntryOnApproval: this.isServiceType(type) && createHistoryEntryOnApproval,
            historyStartDate: this.isServiceType(type) ? historyStartDate : null,
            historyEndDate: this.isServiceType(type) ? historyEndDate : null,
            initialMedia: mediaIris,
            initialMediaDescription: initialMediaDescription || null,
            initialMediaCreateHistoryEntryOnApproval: this.isServiceType(type) && initialMediaCreateHistoryEntryOnApproval,
            initialMediaHistoryStartDate: this.isServiceType(type) ? initialMediaHistoryStartDate : null,
            initialMediaHistoryEndDate: this.isServiceType(type) ? initialMediaHistoryEndDate : null,
            initialMediaHistoryContent: this.isServiceType(type) ? (initialMediaHistoryContentResolved || null) : null,
          };

          return this.collaborationDPS.create(payload);
        }),
        finalize(() => {
          this.isLoading = false;
          if (this.isModalMode) {
            this.subject.next(new ModalStateEvent(ModalState.PROCESS_END));
          }
        })
      )
      .subscribe({
        next: (collaboration) => {
          if (this.mediaUploaderComponent?.fileUploader) {
            this.mediaUploaderComponent.fileUploader.clear();
            this.mediaUploaderComponent.files = [];
            this.mediaUploaderComponent.newFiles = [];
          }
          if (this.isModalMode) {
            this.subject.next(new ModalStateEvent(ModalState.SUCCESS, collaboration));
            return;
          }

          const ownerSlug = collaboration.owner?.slug || this.currentUser?.slug;
          const collaborationKey = collaboration?.slug || collaboration?.id;
          if (ownerSlug && collaborationKey) {
            this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.success'));
            this.router.navigate(['/', ownerSlug, 'collaborations', collaborationKey]);
          }
        },
        error: () => {
          this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.error'));
          this.updateModalValidityState();
        }
      });
  }

  public goBack(): void {
    const fallback = this.currentUser?.slug ? ['/', this.currentUser.slug, 'garage'] : ['/'];
    this.router.navigate(fallback);
  }

  private uploadInitialMedia(): Observable<string[]> {
    const files = this.mediaUploaderComponent?.newFiles || [];
    if (!files.length) {
      return of([]);
    }

    const requests = files.map((file) => {
      const isVideo = file.type?.startsWith('video/');
      return isVideo
        ? this.mediaObjectRS.uploadVideo(file)
        : this.mediaObjectRS.uploadImage(file);
    });

    return forkJoin(requests).pipe(
      map((results: MediaObjectApiModel[]) => results.map((media) => media['@id']))
    );
  }

  private updateModalValidityState(): void {
    if (!this.isModalMode) {
      return;
    }

    if (this.form.valid && !!this.projectOptionControl?.value) {
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }

    this.subject.next(new ModalStateEvent(ModalState.INVALID));
  }

  public copyInitialMediaDescriptionToHistoryContent(): void {
    const summaryDescription = (this.form.get('serviceSummary')?.value || '').trim();
    const mediaDescription = (this.form.get('initialMediaDescription')?.value || '').trim();
    const collaborationDescription = (this.form.get('description')?.value || '').trim();

    this.form.patchValue({
      initialMediaHistoryContent: summaryDescription || mediaDescription || collaborationDescription
    });
  }

  private hasUnsavedChanges(): boolean {
    return !!this.form?.dirty || (this.mediaUploaderComponent?.newFiles?.length || 0) > 0;
  }

  private resolveUserSlug(user: any): string | null {
    if (!user) {
      return null;
    }
    if (typeof user === 'string') {
      return user.split('/').pop() || null;
    }
    return user.slug || null;
  }
}
